/**
 * AccountFlip — панель главного экрана, которая переворачивается со счёта на карту и обратно.
 * Лицевая сторона — привычный счёт (общий баланс и активы), обратная — выпущенная карта.
 * Переключатель показываем только если карта выпущена; без карты это обычная панель счёта.
 *
 * Высоту контейнера тянем за активной стороной (у счёта и карты она разная), поэтому панель
 * не прыгает и не оставляет пустоту под картой. Пропсы: account, usdt, rate, card, showCard,
 * onToggle, onOpenCard (тап по самой карте — на её экран).
 */
import { useEffect, useRef, useState } from "react";
import { AccountCard } from "@/components/AccountCard";
import type { Account, IssuedCard, UsdtAsset } from "@/lib/api";
import { CardFace } from "./CardFace";

type Props = {
  account: Account;
  usdt: UsdtAsset | null;
  rate: number | null;
  card: IssuedCard | null;
  showCard: boolean;
  onToggle: () => void;
  onOpenCard: () => void;
};

export function AccountFlip({ account, usdt, rate, card, showCard, onToggle, onOpenCard }: Props) {
  const frontRef = useRef<HTMLDivElement>(null);
  const backRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | undefined>(undefined);

  // высота = высота видимой стороны; пересчитываем при смене стороны и ресайзе
  useEffect(() => {
    const measure = () => {
      const el = showCard ? backRef.current : frontRef.current;
      if (el) setHeight(el.offsetHeight);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [showCard, card, account, usdt, rate]);

  const flipped = showCard && card != null;

  return (
    <div>
      <div className="flip-scene" style={{ height }}>
        <div className={`flip-inner relative ${flipped ? "is-flipped" : ""}`} style={{ height }}>
          {/* Скрытая сторона не должна ловить тапы: стороны разной высоты, и та, что выше
              (счёт), иначе накрывает собой кнопки под панелью, даже будучи невидимой. */}
          <div
            ref={frontRef}
            className={`flip-face absolute inset-x-0 top-0 ${flipped ? "pointer-events-none" : ""}`}
            aria-hidden={flipped}
          >
            <AccountCard account={account} usdt={usdt} rate={rate} />
          </div>
          {card && (
            <div
              ref={backRef}
              className={`flip-face flip-face-back absolute inset-x-0 top-0 ${flipped ? "" : "pointer-events-none"}`}
              aria-hidden={!flipped}
            >
              <button onClick={onOpenCard} className="block w-full text-left active:opacity-90">
                <CardFace card={card} />
              </button>
            </div>
          )}
        </div>
      </div>

      {card && (
        <button
          onClick={onToggle}
          className="relative z-10 mt-3 flex w-full items-center justify-center gap-1.5 rounded-2xl border border-white/[0.08] bg-white/[0.04] py-2.5 text-[13px] font-medium text-white/70 backdrop-blur-xl active:opacity-80"
        >
          <span className="text-[15px] leading-none">⇄</span>
          {showCard ? "Показать счёт" : "Показать карту"}
        </button>
      )}
    </div>
  );
}
